import { useEffect, useRef, useState } from 'react';
import {
  getLocalStorageValue,
  prefixStorageKey,
  setLocalStorageValue,
} from '@antimatterhq/ui/utils/localStorage';
import { parseBase64Json } from '@antimatterhq/ui/utils/parse';

export type JSONError = {
  [key: string]: string;
  message: string;
  identifier: string;
  resourceType: string;
};

export class RedirectError extends Error {
  message: string;

  identifier: string;

  resourceType: string;

  constructor(message: string, identifier: string, resourceType: string) {
    super(message);
    this.name = 'RedirectError';
    this.message = message;
    this.identifier = identifier;
    this.resourceType = resourceType;
  }
}

const REDIRECT_LOOP_TIME_THRESHOLD = 60 * 1000; // 60 seconds
const MAX_STORED_REDIRECTS = 5; // Limit stored redirects to prevent excessive storage

export const useRedirectHandler = ({ prefix }: { prefix: string }) => {
  const isCheckingRedirectRef = useRef<boolean>(false);
  const [isCheckingRedirect, setIsCheckingRedirect] = useState<
    boolean | undefined
  >(undefined);
  const [redirectError, setRedirectError] = useState<RedirectError | null>(
    null,
  );

  // Did we just load the page after successful auth redirect?
  useEffect(() => {
    if (isCheckingRedirectRef.current) return;
    isCheckingRedirectRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const isRedirect =
      !!params.get('error') ||
      (!!params.get('amworkspaceid') &&
        !!params.get('expiry') &&
        !!params.get('admin'));

    if (!isRedirect) {
      setIsCheckingRedirect(false);
      return;
    }

    setIsCheckingRedirect(true);

    const error = params.get('error');

    // If there's an error, we need to clear the instance ID
    if (error !== null) {
      const errorJson = parseBase64Json<JSONError>(error);

      window.history.replaceState({}, '', window.location.pathname);

      setRedirectError(
        new RedirectError(
          errorJson.message
            ? errorJson.message.charAt(0).toUpperCase() +
              errorJson.message.slice(1)
            : 'An unknown error occurred while authenticating',
          errorJson.identifier,
          errorJson.resourceType,
        ),
      );
    } else {
      // If there's no error, we need to check for redirect loops
      const currentTime = Date.now();

      // Clean up any invalid expiries (ensure they're numbers)
      let previousRedirectTimestamps =
        getLocalStorageValue<number[]>(
          prefixStorageKey('previousRedirectTimestamps', prefix),
        ) || [];

      // Clean up any invalid expiries (ensure they're numbers)
      previousRedirectTimestamps = previousRedirectTimestamps.filter(
        (exp): exp is number => typeof exp === 'number' && !isNaN(exp),
      );

      // Check for redirect loops
      if (previousRedirectTimestamps.length >= 2) {
        // Compare the time between redirects - we're looking for 3+ redirects in < 60 seconds
        const timeWindowStart = currentTime - REDIRECT_LOOP_TIME_THRESHOLD;
        const recentRedirects = previousRedirectTimestamps.filter(
          (time) => time > timeWindowStart,
        );

        // If we have 2 or more redirects in the last 60 seconds, we have a redirect loop
        if (recentRedirects.length >= 2) {
          window.history.replaceState({}, '', window.location.pathname);

          setRedirectError(
            new RedirectError(
              'Redirect Loop Detected',
              'TooManyRedirects',
              'Redirect',
            ),
          );
        }
      }

      // Store this redirect timestamp
      if (!previousRedirectTimestamps.includes(currentTime)) {
        const updatedExpiries = [
          currentTime,
          ...previousRedirectTimestamps,
        ].slice(0, MAX_STORED_REDIRECTS); // Limit array size

        // Store the updated expiries
        setLocalStorageValue(
          prefixStorageKey('previousRedirectTimestamps', prefix),
          updatedExpiries,
        );
      }
    }

    // remove query params from url
    window.history.replaceState({}, '', window.location.pathname);

    isCheckingRedirectRef.current = false;
    setIsCheckingRedirect(false);

    // we only want this to run once per page load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { isCheckingRedirect, redirectError };
};
